import React from 'react'
import { styled, css, type CSSRules } from 'styled-components'
import { PROPORTION_COLORS } from './constants'
import { cssPlayerHeightUnit } from '../../../../utility/constants'
import type { Score } from './types'

/**
 * HexagonOverlay - shows the percentage in a hexagon which moves out of the way when hovers
 *
 * @param  {...Score} props - the score percentage to show in the hexagon
 *
 * @returns React$Node
 */
export function HexagonOverlay({ score }: { score: ?Score }): React$Node {
	let color = PROPORTION_COLORS[0].color
	let isGlowing = false
	if (score?.outOf) {
		const currentProportion = score.correct / score.outOf
		for (let i = 0; i < PROPORTION_COLORS.length; i++) {
			if (currentProportion < PROPORTION_COLORS[i].proportion) {
				break
			}
			color = PROPORTION_COLORS[i].color
			isGlowing = PROPORTION_COLORS[i].isGlowing || false
		}
	} else {
		color = PROPORTION_COLORS[PROPORTION_COLORS.length - 1].color
	}

	return (
		<StyledHoverApplier>
			<HexagonPositioner>
				<HexagonTop color={color} isGlowing={isGlowing} />
				<HexagonMiddle color={color} isGlowing={isGlowing} />
				<HexagonBottom color={color} isGlowing={isGlowing} />
				<HexagonTextPositioner>
					{score?.outOf
						? `${Math.floor(((score.correct || 0) / score.outOf) * 100)}%`
						: 'N/A'}
				</HexagonTextPositioner>
			</HexagonPositioner>
		</StyledHoverApplier>
	)
}

const HEXAGON_SIDE_WIDTH = 5
const HEXAGON_WIDTH =
	Math.cos(((120 - 90) / 180) * Math.PI) * HEXAGON_SIDE_WIDTH * 2
const HEXAGON_TRIANGLE_HEIGHT =
	Math.sin(((120 - 90) / 180) * Math.PI) * HEXAGON_SIDE_WIDTH
const TRANSITION_SPEED = '0.5s'
const TRANSITION_DELAY = '0.2s'

const HexagonPositioner = styled.div`
	position: absolute;
	width: min-content;
	transition: right ${TRANSITION_SPEED}, bottom ${TRANSITION_SPEED},
		transform ${TRANSITION_SPEED}, opacity ${TRANSITION_SPEED};
	transition-delay: ${TRANSITION_DELAY};
	right: calc(50% - ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)});
	bottom: calc(50% - ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)});
	transform-origin: bottom right;
`

const HexagonMiddle = styled.div`
	width: ${cssPlayerHeightUnit(HEXAGON_WIDTH)};
	height: ${cssPlayerHeightUnit(HEXAGON_SIDE_WIDTH)};
	background-color: ${({ color }) => color};
	${determineAnimation}
`

const HexagonTextPositioner = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	color: white;
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: ${cssPlayerHeightUnit(3.5)};
`

const HexagonTop = styled.div`
	height: 0;
	width: 0;
	border-bottom: ${cssPlayerHeightUnit(HEXAGON_TRIANGLE_HEIGHT)} solid
		${({ color }) => color};
	border-left: ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)} solid transparent;
	border-right: ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)} solid transparent;
	${determineAnimation}
`

const HexagonBottom = styled.div`
	content: '';
	width: 0;
	border-top: ${cssPlayerHeightUnit(HEXAGON_TRIANGLE_HEIGHT)} solid
		${({ color }) => color};
	border-left: ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)} solid transparent;
	border-right: ${cssPlayerHeightUnit(HEXAGON_WIDTH / 2)} solid transparent;
	${determineAnimation}
`

const StyledHoverApplier = styled.div`
	width: 100%;
	height: 100%;

	transition: background-color ${TRANSITION_SPEED};
	transition-delay: ${TRANSITION_DELAY};
	background-color: #14163dbf;

	&:hover {
		background-color: #14163d00;
		cursor: pointer;
	}

	&:hover ${HexagonPositioner} {
		right: ${cssPlayerHeightUnit(1)};
		bottom: ${cssPlayerHeightUnit(1)};
		transform: scale(40%);
	}

	&:hover * {
		opacity: 0.75;
	}

	& * {
		transition-delay: ${TRANSITION_DELAY};
	}

	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
`

function determineAnimation({
	isGlowing,
}: {
	isGlowing: boolean,
}): CSSRules | string {
	if (isGlowing) {
		return glowingCss
	}
	return ''
}

const glowingCss: CSSRules = css`
	animation: hexagon-glow 2s infinite alternate;
	@keyframes hexagon-glow {
		from {
			filter: brightness(100%);
		}
		to {
			filter: brightness(200%);
		}
	}
`
